/**
 * Minimal i18n. English default, Persian toggle.
 *
 * Deliberately not a library: the string count is small, and a dependency would
 * cost more bundle than it saves on a mobile-first app.
 *
 * Direction is the subtle part. Switching to Persian flips the *page* direction
 * so the chrome (nav, buttons, lists) reads right-to-left — but card content is
 * always wrapped per text node via persian.ts's `dirAttrs`, never inheriting from
 * the page. An EN↔FA app is inherently mixed-direction: English inside an RTL
 * page renders with its punctuation at the wrong end unless each node declares
 * itself.
 */

export type Lang = "en" | "fa";

const STORAGE_KEY = "lexa.lang";

/**
 * `enOne`/`faOne` are the singular forms, selected when a `n` param resolves to
 * the "one" plural category. Persian generally doesn't inflect a noun after a
 * numeral ("۱ کارت" / "۸ کارت"), so `faOne` is usually unnecessary — English is
 * the one that needs it, and "1 decks" is the bug this prevents.
 */
type Entry = { en: string; fa: string; enOne?: string; faOne?: string };

const STRINGS = {
  // --- app chrome ---
  appName: { en: "Lexa", fa: "لکسا" },
  tagline: { en: "Learn English, one card at a time", fa: "انگلیسی را کارت به کارت یاد بگیرید" },
  navHome: { en: "Home", fa: "خانه" },
  navBrowse: { en: "Browse", fa: "مرور" },
  navStudy: { en: "Study", fa: "مطالعه" },
  navStats: { en: "Stats", fa: "آمار" },
  navSettings: { en: "Settings", fa: "تنظیمات" },
  back: { en: "Back", fa: "بازگشت" },
  cancel: { en: "Cancel", fa: "لغو" },
  save: { en: "Save", fa: "ذخیره" },
  delete: { en: "Delete", fa: "حذف" },
  edit: { en: "Edit", fa: "ویرایش" },
  close: { en: "Close", fa: "بستن" },
  loading: { en: "Loading…", fa: "در حال بارگذاری…" },
  retry: { en: "Try again", fa: "تلاش دوباره" },

  // --- auth ---
  signIn: { en: "Sign in", fa: "ورود" },
  signOut: { en: "Sign out", fa: "خروج" },
  emailLabel: { en: "Email address", fa: "نشانی ایمیل" },
  emailPlaceholder: { en: "you@example.com", fa: "you@example.com" },
  sendCode: { en: "Email me a code", fa: "کد را برایم ایمیل کن" },
  codeLabel: { en: "6-digit code", fa: "کد ۶ رقمی" },
  verifyCode: { en: "Verify & sign in", fa: "تأیید و ورود" },
  codeSent: { en: "Check your email for a 6-digit code.", fa: "ایمیل خود را برای کد ۶ رقمی بررسی کنید." },
  signInPitch: {
    en: "Sign in to save your progress across devices. No password needed.",
    fa: "برای ذخیره پیشرفت در همه دستگاه‌ها وارد شوید. نیازی به گذرواژه نیست.",
  },

  // --- collections ---
  collections: { en: "Collections", fa: "مجموعه‌ها" },
  decks: { en: "Decks", fa: "دسته‌ها" },
  myCollections: { en: "My collections", fa: "مجموعه‌های من" },
  official: { en: "Featured", fa: "پیشنهادی" },
  mine: { en: "Mine", fa: "مال من" },
  newCollection: { en: "New collection", fa: "مجموعه جدید" },
  newDeck: { en: "New deck", fa: "دسته جدید" },
  collectionTitle: { en: "Title", fa: "عنوان" },
  collectionTitleFa: { en: "Persian title (optional)", fa: "عنوان فارسی (اختیاری)" },
  collectionDescription: { en: "Description (optional)", fa: "توضیحات (اختیاری)" },
  collectionColor: { en: "Color", fa: "رنگ" },
  editCollection: { en: "Edit collection", fa: "ویرایش مجموعه" },
  cardCount: { en: "{n} cards", enOne: "{n} card", fa: "{n} کارت" },
  deckCount: { en: "{n} decks", enOne: "{n} deck", fa: "{n} دسته" },
  emptyCollection: { en: "No cards yet.", fa: "هنوز کارتی نیست." },
  noCollections: {
    en: "Nothing here yet. Create a collection or add a featured one.",
    fa: "هنوز چیزی نیست. یک مجموعه بسازید یا از پیشنهادی‌ها اضافه کنید.",
  },
  add: { en: "Add", fa: "افزودن" },
  added: { en: "Added", fa: "افزوده شد" },
  // Deliberately NOT "Remove": this drops your enrollment and leaves the content
  // untouched, and the old label was indistinguishable from deleting. Nor
  // "Remove from collection" — Collection is a noun here, so that reads as
  // detaching a deck from its parent, which is a different and destructive thing.
  stopStudying: { en: "Stop studying", fa: "توقف مطالعه" },
  share: { en: "Share", fa: "هم‌رسانی" },
  shareCopied: { en: "Link copied", fa: "پیوند کپی شد" },
  fork: { en: "Make a copy", fa: "ساختن رونوشت" },
  deleteCollection: { en: "Delete collection", fa: "حذف مجموعه" },
  deleteDeck: { en: "Delete deck", fa: "حذف دسته" },
  deletePermanently: { en: "Delete permanently", fa: "حذف دائمی" },
  // Names the blast radius rather than asking "are you sure?". The progress
  // warning is unconditional because enrollments RLS only exposes your own rows,
  // so the client genuinely cannot tell whether anyone else is studying this.
  deleteWarning: {
    en: "Deletes {n} cards, and the study progress of everyone using them. This cannot be undone.",
    enOne: "Deletes {n} card, and the study progress of everyone using it. This cannot be undone.",
    fa: "{n} کارت و پیشرفت مطالعه همه کسانی که از آن‌ها استفاده می‌کنند حذف می‌شود. این عمل بازگشت‌پذیر نیست.",
  },
  // Singular only — this is always exactly one card, unlike deleteWarning.
  deleteCardWarning: {
    en: "Deletes this card, and the study progress of everyone reviewing it. This cannot be undone.",
    fa: "این کارت و پیشرفت مطالعه همه کسانی که آن را مرور می‌کنند حذف می‌شود. این عمل بازگشت‌پذیر نیست.",
  },
  spoilerWarning: {
    en: "Contains spoilers for this episode.",
    fa: "حاوی لو رفتن داستان این قسمت است.",
  },
  spoilerConfirm: { en: "I've watched it — continue", fa: "دیده‌ام — ادامه" },

  // --- study ---
  studyNow: { en: "Study now", fa: "شروع مطالعه" },
  dueToday: { en: "{n} due", fa: "{n} برای مرور" },
  newToday: { en: "{n} new", fa: "{n} تازه" },
  progress: { en: "Progress", fa: "پیشرفت" },
  percentValue: { en: "{n}%", fa: "٪{n}" },
  startedOn: { en: "Started {when}", fa: "شروع {when}" },
  statsStreak: { en: "day streak", fa: "روز پیاپی" },
  statsTotalReviewed: { en: "cards reviewed", fa: "کارت مرور شده" },
  tapToReveal: { en: "Tap to reveal", fa: "برای دیدن پاسخ بزنید" },
  hint: { en: "Hint", fa: "راهنمایی" },
  knewIt: { en: "I knew it", fa: "می‌دانستم" },
  didntKnow: { en: "Didn't know", fa: "نمی‌دانستم" },
  undo: { en: "Undo", fa: "بازگردانی" },
  // Named for the card's SIDES, not for languages. A collection carries its own
  // `lang_front` / `lang_back`, so a user-made deck need not be English/Persian
  // at all — and these labels stay correct for whatever pair it holds.
  //
  // Naming only the side that leads, never "X → Y": an arrow between two Persian
  // words is mirrored by the RTL layout and ends up contradicting itself.
  coverFirst: { en: "Cover first", fa: "ابتدا رو" },
  backFirst: { en: "Back first", fa: "ابتدا پشت" },
  alreadyKnow: { en: "I already know this", fa: "این را از قبل می‌دانم" },
  // Short forms: these label the swipe badge, which has one line inside the card.
  alreadyKnowShort: { en: "Already know", fa: "می‌دانم" },
  revealAnswer: { en: "Reveal", fa: "نمایش پاسخ" },
  cardRetired: { en: "Removed from your studies", fa: "از مطالعه شما حذف شد" },
  nextReview: { en: "Next review {when}", fa: "مرور بعدی {when}" },
  sessionDone: { en: "All done for now", fa: "برای حالا تمام شد" },
  keepGoing: { en: "Keep going", fa: "ادامه بده" },
  keepGoingBody: {
    en: "You have hit today's limit of new words. You can carry on if you want to.",
    fa: "به سقف امروز برای واژه‌های تازه رسیده‌اید. اگر بخواهید می‌توانید ادامه دهید.",
  },
  moreAvailable: { en: "{n} more waiting", enOne: "{n} more waiting", fa: "{n} مورد دیگر باقی مانده" },
  cardPreview: { en: "Card", fa: "کارت" },
  senseHere: { en: "In this deck", fa: "در این دسته" },
  senseGeneral: { en: "Usually means", fa: "معنی رایج" },
  myNote: { en: "My note", fa: "یادداشت من" },
  notePlaceholder: { en: "Anything that helps you remember…", fa: "هر چیزی که به یادآوری کمک کند…" },
  noteSaved: { en: "Saved", fa: "ذخیره شد" },
  confirmDelete: { en: "Delete this? This cannot be undone.", fa: "حذف شود؟ این عمل بازگشت‌پذیر نیست." },
  sessionDoneBody: {
    en: "Nothing else is due. Come back later and the schedule will have more for you.",
    fa: "چیز دیگری برای مرور نیست. بعداً برگردید تا برنامه موارد بیشتری داشته باشد.",
  },
  reviewedCount: { en: "{n} reviewed", fa: "{n} مرور شد" },
  playAudio: { en: "Play pronunciation", fa: "پخش تلفظ" },

  // --- cards / editor ---
  front: { en: "English", fa: "انگلیسی" },
  backSide: { en: "Persian", fa: "فارسی" },
  example: { en: "Example sentence", fa: "جمله نمونه" },
  notes: { en: "Notes", fa: "یادداشت" },
  tags: { en: "Tags", fa: "برچسب‌ها" },
  addCard: { en: "Add card", fa: "افزودن کارت" },
  duplicateWarning: { en: "This looks like a duplicate.", fa: "این تکراری به نظر می‌رسد." },

  // --- import ---
  importTitle: { en: "Bulk import", fa: "ورود گروهی" },
  importHelp: {
    en: "Paste rows as English⇥Persian, one per line. Tab or comma separated.",
    fa: "ردیف‌ها را به شکل انگلیسی⇥فارسی، هر خط یکی، بچسبانید. با تب یا ویرگول جدا شود.",
  },
  importPreview: { en: "Preview", fa: "پیش‌نمایش" },
  importRun: { en: "Import {n} cards", fa: "ورود {n} کارت" },
  importDone: { en: "Imported {n} cards, skipped {skipped}.", fa: "{n} کارت وارد شد، {skipped} رد شد." },

  // --- settings ---
  language: { en: "Language", fa: "زبان" },
  newPerDay: { en: "New cards per day", fa: "کارت‌های تازه در روز" },
  autoplayAudio: { en: "Play pronunciation automatically", fa: "پخش خودکار تلفظ" },
  timezone: { en: "Time zone", fa: "منطقه زمانی" },

  // --- errors ---
  errOffline: {
    en: "You're offline. Reviews are saved and will sync when you reconnect.",
    fa: "آفلاین هستید. مرورها ذخیره می‌شوند و با اتصال مجدد هم‌گام می‌شوند.",
  },
  errGeneric: { en: "Something went wrong.", fa: "خطایی رخ داد." },
  errNotSignedIn: { en: "Sign in first.", fa: "ابتدا وارد شوید." },
} satisfies Record<string, Entry>;

export type StringKey = keyof typeof STRINGS;

function readStored(): Lang | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "en" || v === "fa" ? v : null;
  } catch {
    return null; // private mode / storage disabled
  }
}

let current: Lang = readStored() ?? "en";

const listeners = new Set<(lang: Lang) => void>();

export function lang(): Lang {
  return current;
}

export function isRtl(): boolean {
  return current === "fa";
}

/**
 * Translate. `{name}` placeholders are substituted from `params`.
 *
 * Numeric params are localized here (۱۲۳ vs 123) so callers pass raw numbers and
 * digit formatting has exactly one home. Passing a pre-formatted string would
 * also defeat plural selection, since "1" is not `1`.
 *
 * Returns the key itself when missing, so gaps are visible in the UI rather than
 * rendering as empty space.
 */
export function t(key: StringKey, params?: Record<string, string | number>): string {
  const entry: Entry | undefined = STRINGS[key];
  if (!entry) return key;

  const count = typeof params?.n === "number" ? params.n : null;
  const isOne =
    count !== null &&
    new Intl.PluralRules(current === "fa" ? "fa-IR" : "en-US").select(count) === "one";

  let text =
    (isOne ? (current === "fa" ? entry.faOne : entry.enOne) : undefined) ??
    entry[current] ??
    entry.en;

  if (params) {
    for (const [name, value] of Object.entries(params)) {
      text = text.replaceAll(`{${name}}`, typeof value === "number" ? num(value) : value);
    }
  }
  return text;
}

/** Localize digits for display. Persian readers expect ۱۲۳, not 123. */
export function num(value: number): string {
  return new Intl.NumberFormat(current === "fa" ? "fa-IR" : "en-US").format(value);
}

export function setLang(next: Lang): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Non-fatal: the choice just won't survive a reload.
  }
  applyToDocument();
  for (const listener of listeners) listener(next);
}

export function toggleLang(): void {
  setLang(current === "en" ? "fa" : "en");
}

export function onLangChange(fn: (lang: Lang) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Sets lang/dir on <html>. Chrome flips with the language; card content does not
 * inherit this — see the module comment.
 */
export function applyToDocument(): void {
  const html = document.documentElement;
  html.lang = current;
  html.dir = current === "fa" ? "rtl" : "ltr";
}
